import { Component, Input, ViewChild, Output, EventEmitter } from '@angular/core';
import { IDictionary } from '../../model/models/dictionary.interface';
import { FormControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { APP_CONFIG } from '../../environment';
import { TranslateService } from '@ngx-translate/core';
import { ProviderUtilities } from '../../providers/provider-utilities';

@Component({
  selector: 'rmt-date',
  templateUrl: 'rmt-date.html'
})
export class RmtDateComponent {

  @ViewChild('day') day;
  @ViewChild('month') month;
  @ViewChild('year') year;
  @ViewChild('input') input;
  @Input() disable: boolean = false; // unfortunately we can't use the name disabled otherwise we could get reactive form warnings
  @Input() showDays: boolean = true;
  @Input() fullYear: boolean = true; // 'true' for 4 digit year format and 'false' for 2 digit year format
  @Input() enableAutoLabels: boolean = true;
  @Input() newDesign: boolean = false;
  @Input() inputId: string="";
  @Input() control: FormControl;
  @Input() errorMessage;
  @Input() defaultErrors: boolean = false;
  @Input() refFormControlError;
  @Input() hideError: boolean = true;
  @Input() preserveDateOnFocus: boolean = false;
  @Output() ionChange = new EventEmitter<IDictionary>();
  @Input() isResetVal: boolean = false;

  placeholder: boolean = true;
  months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  fields = {Day: 'day',
            Month: 'month',
            Year: 'year'};
  dateFieldsOrder = [];
  dateFormat: string;
  formGroup: FormGroup;
  cleanField: boolean = false;
  dateTimeOut;

  constructor(private translate: TranslateService, private formBuilder: FormBuilder) {}

  ngOnInit() {

    if(!this.disable)
      this.disable = false;
    this.initDateFormat();
    this.parseDateFormat();
    this.initMonths();
    this.initDate();
  }
  
  ngAfterViewInit() {
    this.input.getNativeElement().querySelector('input').addEventListener('change', this.changeInput.bind(this))
  }

  initDateFormat() {
    if (this.showDays) {
      this.dateFormat = APP_CONFIG.region.dateFormat;
    }
    else if (this.fullYear) {
      this.dateFormat = 'MM/YYYY';
    }
    else {
      this.dateFormat = 'MM/YY';
    }
  }

  initMonths() {
    const months = this.translate.instant('global.myRia.jsonContent.MonthList');
    if (months) {
      const splitted = months.split(',');
      if (splitted.length === 12)
        this.months = splitted;
    }
  }

  initDate() {
    let date = null;
    if(this.control && this.control.value){
      date = ProviderUtilities.parseDate(this.control.value.substr(0,10));
    }else if(this.input && this.input.value){
      date = ProviderUtilities.parseDate(this.input.value.substr(0,10));
    }

    if(date){
      let day = date.getDate().toString();
      if( this.formGroup.controls[this.fields.Day])
        this.formGroup.controls[this.fields.Day].setValue(day.length < 2 ? ProviderUtilities.preFill(day): day);
      let month = (date.getMonth()+1).toString();
      this.formGroup.controls[this.fields.Month].setValue(month.length < 2 ? ProviderUtilities.preFill(month):month);
      if (this.fullYear)
        this.formGroup.controls[this.fields.Year].setValue(date.getFullYear().toString());
      else
        this.formGroup.controls[this.fields.Year].setValue(date.getFullYear().toString().substr(-2));
    }
  }

  keyDownDate(ev, index) {
    if(index > 0 && ev.keyCode == 8 && ev.srcElement && !ev.srcElement.value) {
      let prevField = this.dateFieldsOrder[index-1][0];
      this.cleanField = false;
      if(this[prevField]) {
        this[prevField].inputField.nativeElement.focus();
        this.day.nativeElement.blur();
      }
    }
  }

  changeInput(ev, index) {
    let name = this.dateFieldsOrder[index][0];
    let nextField = null;
    if(Object.keys(this.formGroup.controls).length-1 > index)
      nextField = this.dateFieldsOrder[index+1][0];

    if(ev.max && Number(ev.value) > Number(ev.max)){
      ev.value = ProviderUtilities.preFill(ev.max, ev.max.length);
    }
    else if(ev.min && (ev.value.length == ev.min.length+1 || ev.value.length == 4) && Number(ev.value) < Number(ev.min)){
      ev.value = ProviderUtilities.preFill(ev.min, ev.min.length);
      if(nextField)
      this[nextField].inputField.nativeElement.focus();
    }
    else if(ev.max && ev.value.length == ev.max.length && nextField){
        if (index < this.dateFieldsOrder.length-1)
        this.dateFieldsOrder.forEach(fields => { fields[2] = false });
        this.cleanField=false;
        this[nextField].inputField.nativeElement.focus();
    }
    if(this.formGroup.valid && this.formGroup.controls[name].value != ""){
      this.changeDate();
    }

    if(ev.value === '' && this.isResetVal) { // set reset value based on parent value
      if (this.formGroup && this.formGroup.controls) {
        const controls = this.formGroup.controls;

        if (controls[this.fields.Day]) {
          controls[this.fields.Day].reset();
        }
        if (controls[this.fields.Month]) {
          controls[this.fields.Month].reset();
        }
        if (controls[this.fields.Year]) {
          controls[this.fields.Year].reset();
        }

      }
      this.control.reset();
    }
  }

  checkError() {
    return this.control && this.control.touched && !this.control.valid;
  }

  changeDate(expirationDate?) {
    if(expirationDate){
      this.formGroup.controls[this.fields.Year].setValue(expirationDate[this.fields.Year]);
      this.formGroup.controls[this.fields.Month].setValue(expirationDate[this.fields.Month]);
    }

    window.setTimeout(() => {
      let date = '';
      if(this.control)
        this.control.markAsTouched();

      let year = this.formGroup.controls[this.fields.Year].value;
      if (year.length === 2)
        year = '20' + year;
      date = year + '-' + ProviderUtilities.preFill(this.formGroup.controls[this.fields.Month].value);
      if (this.showDays) {
        let day = ProviderUtilities.preFill(this.formGroup.controls[this.fields.Day].value);
        date += '-' + day;
      }

      let inputString = "";
      if(this.control){
          inputString = this.control.value.toString().substr(0,10);
      }else if(this.input){
        inputString = this.input.value.toString().substr(0,10);
      }

      if (inputString !== date && this.formGroup.valid){
        if(this.control){
          this.control.setValue(date);
        }else if(this.input){
          this.input.value = date;
        }
        this.ionChange.emit({ text: null, value: date });
      }
    }, 400);
  }

  parseDateFormat() {
    this.dateFieldsOrder = [];
    const dates = this.dateFormat.split('/');
    this.formGroup = this.formBuilder.group({});

    for (let i=0; i < dates.length; i++) {
      let date = dates[i];
      if (date)
        date = date.toUpperCase();

      if (date === 'DD'){
        this.formGroup.addControl(this.fields.Day, new FormControl('', Validators.compose([Validators.required, Validators.pattern(`^([1-9]{1}|[0-9]{2})$`)])));
        this.dateFieldsOrder.push([this.fields.Day, this.translate.instant('form.dateForm.day.placeholder'), i==0 ? false : true, this.translate.instant('form.dateForm.day.label')]);
      }else if (date === 'MM'){
        this.formGroup.addControl(this.fields.Month, new FormControl('', Validators.compose([Validators.required, Validators.pattern(`^([1-9]{1}|[0-9]{2})$`)])));
        this.dateFieldsOrder.push([this.fields.Month, this.translate.instant('form.dateForm.month.placeholder'),  i==0 ? false : true, this.translate.instant('form.dateForm.month.label')]);
      }else if (date === 'YY'){
        this.formGroup.addControl(this.fields.Year, new FormControl('', Validators.compose([Validators.required, Validators.pattern(`^[0-9]{2}$`)])));
        this.dateFieldsOrder.push([this.fields.Year, this.translate.instant('form.dateForm.year.additionalText'),  i==0 ? false : true, this.translate.instant('form.dateForm.year.label')]);
      }else if (date === 'YYYY'){
        this.formGroup.addControl(this.fields.Year, new FormControl('', Validators.compose([Validators.required, Validators.pattern(`^[0-9]{4}$`)])));
        this.dateFieldsOrder.push([this.fields.Year, this.translate.instant('form.dateForm.year.placeholder'),  i==0 ? false : true, this.translate.instant('form.dateForm.year.label')]);
      }
    }
    this.formGroup.updateValueAndValidity();
  }

  onFocus(ev, index){
    if (this.preserveDateOnFocus)
      return;

    if(this.cleanField){
      let name = this.dateFieldsOrder[index][0];
      this.formGroup.controls[name].setValue("");
      if(this.control){
        this.control.setValue("");
        this.control.updateValueAndValidity();
      }
    }
    if(this.dateTimeOut)
      window.clearTimeout(this.dateTimeOut);

    // if(this.control)
    //   this.control.markAsTouched();

    let minIndex = index;
    while (minIndex > 0 && this.dateFieldsOrder[index][2]){
      minIndex--;
    }
    if (minIndex !== index) {
      // let prevFocus = this.dateFieldsOrder[minIndex][0];
      // const currFocus = this.dateFieldsOrder[index][0];
      // if(this[prevFocus]) {
      //   this.dateTimeOut = window.setTimeout(() => {
      //     this[currFocus].inputField.nativeElement.blur();
      //     this[prevFocus].inputField.nativeElement.focus()
      //   }, 50);

      // }
      this.cleanField=false;
    }
    else
      this.cleanField=true;

  }

  onBlur(ev, index){
    if(this.control)
      this.control.markAsTouched();
    if(ev.value && 0 < ev.value.length) {
      ev.value = ProviderUtilities.preFill(ev.value);
    }

    if (this.preserveDateOnFocus)
      this.validateEmptyFields();
  }

  getColClass(control: any, index) {
    let colClass = '';
    colClass = (control && control.touched && !control.valid) ? 'rmt-date-field rmt-error' :  'rmt-date-field';
    if(this.newDesign) {
      if(index == 0) colClass += ' rmt-date-field--padding-right';
      else if(index == 1) colClass += ' rmt-date-field--padding';
      else if(index == 2) colClass += ' rmt-date-field--padding-left';
    }

    return colClass;
  }

  getError(control) {
    let error = control && control.touched && !control.valid ;
    return error;
  }

  validateEmptyFields() {
    const controls = this.formGroup.controls;
    const fieldsEmpty = Object.keys(controls).some(key => {
      return !controls[key].value || controls[key].value.toString().trim() === '';
    });

    if (fieldsEmpty && this.control) {
      this.control.setValue("");
      this.control.updateValueAndValidity();
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/rmt-date/rmt-date.ts